import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import PillInput from "./components/PillInput";
import RangeSlider from "./components/RangeSlider";
import TermToggle from "./components/TermToggle";
import RepaymentResults from "./components/RepaymentResults";
import RepresentativeExample from "./components/RepresentativeExample";
import {
  getAprForAmount,
  formatMoney,
  computePaymentNominal,
} from "./helpers/calculations";
import { useTermLength } from "./hooks";
import "./styles/style.scss";

const RepaymentCalculator = ({
  amountMin,
  amountMax,
  amountStep,
  amountDefault,
  monthsMin,
  monthsMax,
  monthsStep,
  monthsDefault,
  weeksMin,
  weeksMax,
  weeksStep,
  weeksDefault,
  aprTiers, // Add aprTiers to the destructured props
}) => {
  const [amount, setAmount] = useState(amountDefault);

  // Custom hook for managing term state.
  const { termType, setTermType, termLength, setTermLength } = useTermLength(
    monthsDefault,
    weeksDefault
  );

  // Define default APR tiers to use as fallback
  const defaultAprTiers = useMemo(
    () => [
      { limit: 2000, apr: 42.6 },
      { limit: 10000, apr: 26.8 },
    ],
    []
  );

  // Handle APR tiers with validation
  const validAprTiers = useMemo(() => {
    // Validate the aprTiers prop
    if (aprTiers && Array.isArray(aprTiers) && aprTiers.length > 0) {
      // Use provided aprTiers if valid
      return aprTiers;
    }
    // Fall back to defaults
    console.log("Using default APR tiers");
    return defaultAprTiers;
  }, [aprTiers, defaultAprTiers]);

  // Get the APR value based on amount and tiers
  const aprValue = useMemo(() => {
    return getAprForAmount(amount, validAprTiers);
  }, [amount, validAprTiers]);
  // Use useMemo to prevent recalculating payment on every render
  const payment = useMemo(() => {
    return computePaymentNominal(amount, aprValue, termLength, termType);
  }, [amount, aprValue, termLength, termType]);

  // Use useMemo for totalRepayment calculation
  const totalRepayment = useMemo(() => {
    return payment * termLength;
  }, [payment, termLength]);

  // Use useMemo for percentage calculation function
  const getPercentage = useMemo(() => {
    return (value, min, max) => {
      return (value - min) / (max - min);
    };
  }, []);

  // Current term settings based on termType - use useMemo to avoid recalculations
  const termSettings = useMemo(() => {
    return {
      currentTermMin: termType === "weeks" ? weeksMin : monthsMin,
      currentTermMax: termType === "weeks" ? weeksMax : monthsMax,
      currentTermStep: termType === "weeks" ? weeksStep : monthsStep,
      termSuffix: termType === "weeks" ? "Weeks" : "Months",
    };
  }, [
    termType,
    weeksMin,
    monthsMin,
    weeksMax,
    monthsMax,
    weeksStep,
    monthsStep,
  ]);

  return (
    <div className="container">
      {/* Pill inputs row */}
      <div className="mrc-pill-row">
        <PillInput
          label="I'd like to borrow"
          prefix="£"
          value={amount}
          onValueChange={setAmount}
          min={amountMin}
          max={amountMax}
        />
        <PillInput
          label={`Over a period of (${
            termType === "weeks" ? "Weeks" : "Months"
          })`}
          suffix={termSettings.termSuffix}
          value={termLength}
          onValueChange={setTermLength}
          min={termSettings.currentTermMin}
          max={termSettings.currentTermMax}
        />
      </div>

      {/* Sliders row */}
      <div className="mrc-sliders">
        <RangeSlider
          label="Amount"
          bubbleLabel="I'd Like To Borrow"
          min={amountMin}
          max={amountMax}
          step={amountStep}
          value={amount}
          onChange={setAmount}
          getPercentage={getPercentage}
          formatCurrency={true}
          bubbleClassName="mrc-slider-bubble--purple"
        />
        <RangeSlider
          label="Term"
          bubbleLabel="Over A Period Of"
          min={termSettings.currentTermMin}
          max={termSettings.currentTermMax}
          step={termSettings.currentTermStep}
          value={termLength}
          onChange={setTermLength}
          getPercentage={getPercentage}
          bubbleSuffix={termSettings.termSuffix}
          bubbleClassName="mrc-slider-bubble--blue"
        />
      </div>

      {/* Toggle for months/weeks */}
      <TermToggle termType={termType} setTermType={setTermType} />

      {/* Display results */}
      <RepaymentResults
        aprValue={aprValue}
        payment={payment}
        totalRepayment={totalRepayment}
        termType={termType}
        formatMoney={formatMoney}
      />

      {/* Representative example */}
      <RepresentativeExample
        amount={amount}
        termLength={termLength}
        termType={termType}
        payment={payment}
        totalRepayment={totalRepayment}
        formatMoney={formatMoney}
        aprValue={aprValue}
      />
    </div>
  );
};

// Add PropTypes for type checking
RepaymentCalculator.propTypes = {
  amountMin: PropTypes.number.isRequired,
  amountMax: PropTypes.number.isRequired,
  amountStep: PropTypes.number.isRequired,
  amountDefault: PropTypes.number.isRequired,
  monthsMin: PropTypes.number.isRequired,
  monthsMax: PropTypes.number.isRequired,
  monthsStep: PropTypes.number.isRequired,
  monthsDefault: PropTypes.number.isRequired,
  weeksMin: PropTypes.number.isRequired,
  weeksMax: PropTypes.number.isRequired,
  weeksStep: PropTypes.number.isRequired,
  weeksDefault: PropTypes.number.isRequired,
  aprTiers: PropTypes.arrayOf(
    PropTypes.shape({
      limit: PropTypes.number.isRequired,
      apr: PropTypes.number.isRequired,
    })
  ),
};

export default RepaymentCalculator;
